/**
 * BoardView - Trello-style Kanban Board
 * Full drag-and-drop creative organization
 * Epic 007 - Story E007-S002
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBoard, moveCard, deleteCard, updateCard } from '../lib/boardsApi';
import { getPerformanceColor, getPlatformIcon, formatCardMetrics } from '../lib/boardsApi';
import type { BoardDetail, CardWithMetrics, ColumnWithCards } from '../types/boards';

export default function BoardView() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  
  const [board, setBoard] = useState<BoardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Drag state
  const [draggedCard, setDraggedCard] = useState<CardWithMetrics | null>(null);
  const [draggedFromColumn, setDraggedFromColumn] = useState<string | null>(null);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  
  // Modal state
  const [selectedCard, setSelectedCard] = useState<CardWithMetrics | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Load board on mount
  useEffect(() => {
    if (boardId) {
      loadBoard();
    }
  }, [boardId]);

  const loadBoard = async () => {
    if (!boardId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getBoard(boardId);
      setBoard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load board');
    } finally {
      setLoading(false);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, card: CardWithMetrics, columnId: string) => {
    setDraggedCard(card);
    setDraggedFromColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    
    // Style the dragged element
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedCard(null);
    setDraggedFromColumn(null);
    setHoveredColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoveredColumn(columnId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving the column entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.closest(`[data-column-id="${hoveredColumn}"]`)) {
      setHoveredColumn(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string, targetPosition: number) => {
    e.preventDefault();
    
    if (!draggedCard || !board) return;

    // Optimistic UI update
    const oldBoard = { ...board };
    const newColumns = board.columns.map(col => ({
      ...col,
      cards: col.cards.filter(c => c.id !== draggedCard.id)
    }));

    const targetColumn = newColumns.find(c => c.id === targetColumnId);
    if (targetColumn) {
      targetColumn.cards.splice(targetPosition, 0, { ...draggedCard, column_id: targetColumnId });
      // Recalculate positions
      targetColumn.cards.forEach((card, idx) => {
        card.position = idx;
      });
    }

    setBoard({ ...board, columns: newColumns });
    setHoveredColumn(null);

    // API call
    try {
      await moveCard(draggedCard.id, {
        column_id: targetColumnId,
        position: targetPosition
      });
      // Reload to sync with backend
      await loadBoard();
    } catch (err) {
      // Rollback on error
      setBoard(oldBoard);
      alert(err instanceof Error ? err.message : 'Failed to move card');
    }
  };

  const handleCardClick = (card: CardWithMetrics) => {
    setSelectedCard(card);
    setNotesValue(card.notes || '');
    setEditingNotes(false);
  };

  const handleSaveNotes = async () => {
    if (!selectedCard) return;

    try {
      setSavingNotes(true);
      await updateCard(selectedCard.id, { notes: notesValue });
      
      // Update local state
      if (board) {
        const newColumns = board.columns.map(col => ({
          ...col,
          cards: col.cards.map(card => 
            card.id === selectedCard.id ? { ...card, notes: notesValue } : card
          )
        }));
        setBoard({ ...board, columns: newColumns });
        setSelectedCard({ ...selectedCard, notes: notesValue });
      }
      
      setEditingNotes(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!confirm('Delete this card? This cannot be undone.')) return;

    try {
      await deleteCard(cardId);
      
      // Update local state
      if (board) {
        const newColumns = board.columns.map(col => ({
          ...col,
          cards: col.cards.filter(c => c.id !== cardId)
        }));
        setBoard({ ...board, columns: newColumns });
      }
      
      setSelectedCard(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete card');
    }
  };

  // Render card component
  const Card = ({ card, columnId }: { card: CardWithMetrics; columnId: string }) => {
    const metrics = formatCardMetrics(card);
    const perfColor = getPerformanceColor(card.performance_score);
    
    return (
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, card, columnId)}
        onDragEnd={handleDragEnd}
        onClick={() => handleCardClick(card)}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow"
      >
        {/* Card Header */}
        <div className="flex justify-between items-start mb-2">
          <span className="text-lg">{getPlatformIcon(card.platform)}</span>
          {card.performance_score !== null && (
            <span className={`text-xs px-2 py-1 rounded font-medium ${
              perfColor === 'green' ? 'bg-green-100 text-green-700' :
              perfColor === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {card.performance_score}
            </span>
          )}
        </div>

        {/* Card Content */}
        {card.headline && (
          <h4 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
            {card.headline}
          </h4>
        )}

        {/* Thumbnail */}
        {card.thumbnail_url && (
          <img
            src={card.thumbnail_url}
            alt="Creative"
            className="w-full h-24 object-cover rounded mb-2"
          />
        )}

        {/* Metrics */}
        <div className="flex justify-between text-xs text-gray-500">
          <span>ROAS: {metrics.roas}</span>
          <span>CTR: {metrics.ctr}</span>
        </div>
        
        {card.notes && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-600 line-clamp-2">{card.notes}</p>
          </div>
        )}
      </div>
    );
  };

  // Render column component
  const Column = ({ column }: { column: ColumnWithCards }) => {
    const isHovered = hoveredColumn === column.id;
    
    return (
      <div
        data-column-id={column.id}
        onDragOver={(e) => handleDragOver(e, column.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, column.id, column.cards.length)}
        className={`flex-1 min-w-[280px] bg-gray-50 rounded-lg p-4 ${
          isHovered ? 'ring-2 ring-blue-400 bg-blue-50' : ''
        }`}
      >
        {/* Column Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900">{column.name}</h3>
          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
            {column.cards.length}
          </span>
        </div>

        {/* Cards */}
        <div className="space-y-2">
          {column.cards.map((card, index) => (
            <div
              key={card.id}
              onDrop={(e) => handleDrop(e, column.id, index)}
              onDragOver={(e) => e.preventDefault()}
            >
              <Card card={card} columnId={column.id} />
            </div>
          ))}
          
          {/* Drop zone at bottom */}
          {column.cards.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-300 rounded-lg">
              Drop cards here
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="flex gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-1 bg-gray-200 rounded-lg h-96"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !board) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error loading board</h3>
          <p className="text-red-600">{error || 'Board not found'}</p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={loadBoard}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/boards')}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Back to Boards
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/boards')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{board.name}</h1>
              {board.description && (
                <p className="text-sm text-gray-600 mt-1">{board.description}</p>
              )}
            </div>
            {board.is_shared && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                Shared
              </span>
            )}
          </div>
          
          <button
            onClick={() => navigate('/boards')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>

      {/* Board Columns */}
      <div className="flex-1 overflow-x-auto p-8">
        <div className="flex gap-4 h-full">
          {board.columns
            .sort((a, b) => a.position - b.position)
            .map((column) => (
              <Column key={column.id} column={column} />
            ))}
        </div>
      </div>

      {/* Card Details Modal */}
      {selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{getPlatformIcon(selectedCard.platform)}</span>
                  <span className="text-sm text-gray-500 capitalize">{selectedCard.platform}</span>
                  {selectedCard.performance_score !== null && (
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      getPerformanceColor(selectedCard.performance_score) === 'green' ? 'bg-green-100 text-green-700' :
                      getPerformanceColor(selectedCard.performance_score) === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      Score: {selectedCard.performance_score}
                    </span>
                  )}
                </div>
                {selectedCard.headline && (
                  <h2 className="text-xl font-semibold text-gray-900">{selectedCard.headline}</h2>
                )}
              </div>
              <button
                onClick={() => setSelectedCard(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Thumbnail */}
              {selectedCard.thumbnail_url && (
                <img
                  src={selectedCard.thumbnail_url}
                  alt="Creative"
                  className="w-full rounded-lg mb-4"
                />
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">ROAS</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCardMetrics(selectedCard).roas}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">CTR</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCardMetrics(selectedCard).ctr}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Spend</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCardMetrics(selectedCard).spend}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-gray-900">Notes</h3>
                  {!editingNotes && (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {editingNotes ? (
                  <div>
                    <textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                      placeholder="Add notes about this creative..."
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingNotes ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingNotes(false);
                          setNotesValue(selectedCard.notes || '');
                        }}
                        disabled={savingNotes}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedCard.notes || 'No notes yet'}
                  </p>
                )}
              </div>

              {/* Creative Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Creative Details</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Creative ID:</span>
                    <span className="font-mono text-gray-900">{selectedCard.creative_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform:</span>
                    <span className="text-gray-900 capitalize">{selectedCard.platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">
                      {new Date(selectedCard.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteCard(selectedCard.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Card
                </button>
                <button
                  onClick={() => setSelectedCard(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
